import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MobileLayout from "../../components/layout/MobileLayout";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { X as XIcon, Sparkles } from "lucide-react";
import ConfirmBidsModal from "../../components/ConfirmBidsModal";
import BetWarningModal from "../../components/BetWarningModal";
import { BottomBar } from "./JodiBetPage";

/**
 * Crossing: user types digits → all unique pairs auto-generate.
 * A single common ₹ amount input (next to the digits box) is applied
 * to every pair. User can remove individual pairs before placing.
 */
function generatePairs(digitsStr, withJoda) {
  const digits = [...new Set((digitsStr || "").split("").filter(c => /\d/.test(c)))];
  if (digits.length < 2) return [];
  const pairs = [];
  for (let i = 0; i < digits.length; i++) {
    for (let j = 0; j < digits.length; j++) {
      if (i === j && !withJoda) continue;
      pairs.push(digits[i] + digits[j]);
    }
  }
  return [...new Set(pairs)].sort();
}

export default function CrossBetPage({ embedded = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [market, setMarket] = useState(null);
  const [settings, setSettings] = useState({});
  const [digitsStr, setDigitsStr] = useState("");
  const [amount, setAmount] = useState(""); // common amount per pair
  const [withJoda, setWithJoda] = useState(true);
  const [removed, setRemoved] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [warn, setWarn] = useState(null);

  useEffect(() => {
    api.get(`/markets/${id}`).then(m => setMarket(m.data));
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, [id]);

  const minBet = settings.min_bet_cross ?? 5;
  const perAmt = Number(amount) || 0;

  // Reset removal set whenever digits/joda change (fresh canvas)
  useEffect(() => {
    setRemoved(new Set());
  }, [digitsStr, withJoda]);

  const activePairs = useMemo(() => {
    const all = generatePairs(digitsStr, withJoda);
    return all.filter(p => !removed.has(p));
  }, [digitsStr, withJoda, removed]);

  const generatedCount = activePairs.length;
  const total = generatedCount * perAmt;

  function removePair(k) {
    setRemoved(prev => { const n = new Set(prev); n.add(k); return n; });
  }
  function clearAll() {
    setDigitsStr("");
    setAmount("");
    setRemoved(new Set());
  }

  function openReview() {
    if (!market?.is_market_active) {
      setWarn({ type: "closed", title: "मार्केट बंद है", message: "अभी इस मार्केट पर बोली नहीं लगाई जा सकती।" });
      return;
    }
    if (generatedCount === 0) {
      setWarn({ type: "empty", title: "जोड़ी नहीं बनी", message: "कम से कम 2 अलग digits डालें (जैसे 12 या 1234) — जोड़ियाँ अपने आप बन जाएंगी।" });
      return;
    }
    if (perAmt <= 0) {
      setWarn({ type: "empty", title: "अमाउंट नहीं डाला", message: "डिजिट्स के साथ वाले ₹ box में amount डालें।" });
      return;
    }
    if (total < minBet) {
      setWarn({ type: "min", title: "अमाउंट बहुत कम है", message: `क्रॉसिंग के लिए मिनिमम टोटल बोली ₹${minBet} है।`, hint: `अभी आपका टोटल ₹${total} है` });
      return;
    }
    if (total > (user?.wallet_balance || 0)) {
      setWarn({ type: "insufficient", title: "वॉलेट में बैलेंस कम है", message: `वॉलेट में सिर्फ़ ₹${Number(user?.wallet_balance || 0).toFixed(2)} है।`, hint: `बोली का टोटल ₹${total}` });
      return;
    }
    setShowReview(true);
  }

  async function confirmPlace() {
    const payload = {
      market_id: market.id,
      bids: activePairs.map(p => ({
        game_type: "cross_bet", session: null, number: p, amount: perAmt,
      })),
    };
    try {
      setSubmitting(true);
      await api.post("/bids", payload);
      await refresh();
      setShowReview(false);
      toast.success(`✅ ${payload.bids.length} क्रॉसिंग बोली लगी (कुल ₹${total})`);
      navigate("/my-bids");
    } catch (e) { toast.error(e?.response?.data?.detail || "बोली नहीं लगी"); }
    finally { setSubmitting(false); }
  }

  const rows = activePairs.map(p => ({ key: p, label: `क्रॉसिंग: ${p}`, amount: perAmt }));
  const uniqueDigitCount = new Set((digitsStr || "").split("").filter(c => /\d/.test(c))).size;

  return (
    <Wrap embedded={embedded}>
      {/* Digits + Amount side-by-side */}
      <div
        className="rounded-2xl border border-cyan-400/20 bg-black/40 backdrop-blur-sm p-4 mb-3"
        data-testid="cross-digits-section"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] font-black tracking-[0.25em] text-slate-400">
            डिजिट डालें + अमाउंट
          </div>
          <div className="inline-flex items-center gap-1 text-[10px] font-bold text-cyan-300">
            <Sparkles className="w-3 h-3" /> ऑटो जोड़ी
          </div>
        </div>

        <div className="grid grid-cols-5 gap-2">
          {/* Digits (takes 3 cols) */}
          <input
            inputMode="numeric"
            value={digitsStr}
            onChange={(e) => setDigitsStr(e.target.value.replace(/\D/g, "").slice(0, 10))}
            data-testid="cross-digits"
            placeholder="जैसे 1234"
            className="col-span-3 h-12 rounded-xl border border-cyan-400/30 bg-black/50 px-3 text-white font-black text-xl tabular-nums tracking-widest focus:outline-none focus:border-cyan-400 placeholder:text-slate-600 placeholder:text-sm placeholder:font-normal placeholder:tracking-normal"
          />
          {/* Amount (takes 2 cols) */}
          <div className="col-span-2 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-cyan-300 font-black text-lg">₹</span>
            <input
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/\D/g, "").slice(0, 6))}
              data-testid="cross-amount"
              placeholder="अमाउंट"
              className="w-full h-12 rounded-xl border border-cyan-400/30 bg-black/50 pl-7 pr-2 text-right text-white font-black text-lg tabular-nums focus:outline-none focus:border-cyan-400 placeholder:text-slate-600 placeholder:text-sm placeholder:font-normal"
            />
          </div>
        </div>

        <div className="mt-2 text-[10px] text-slate-500 tracking-wide">
          {uniqueDigitCount < 2
            ? "कम से कम 2 अलग digits डालें — जोड़ियाँ अपने आप बन जाएंगी"
            : `${uniqueDigitCount} unique digits · ${generatedCount} जोड़ियाँ auto-generated${perAmt > 0 ? ` · हर जोड़ी ₹${perAmt}` : ""}`}
        </div>

        <div className="text-[11px] font-black tracking-[0.25em] text-slate-400 mt-4 mb-2">जोड़ा ऑप्शन</div>
        <div className="grid grid-cols-2 gap-2">
          {[
            { k: true,  label: "जोड़ा के साथ" },
            { k: false, label: "जोड़ा के बिना" },
          ].map(opt => {
            const active = withJoda === opt.k;
            return (
              <button
                key={String(opt.k)}
                onClick={() => setWithJoda(opt.k)}
                data-testid={`cross-joda-${opt.k ? "with" : "without"}`}
                className={`h-11 rounded-xl font-black text-sm transition active:scale-95 ${
                  active
                    ? "text-cyan-200 border-2 border-cyan-400 bg-cyan-400/10 shadow-[0_0_12px_rgba(56,189,248,0.5)]"
                    : "text-slate-300 border border-cyan-400/20 bg-black/40"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Auto generated pairs — read-only tiles, user can only remove */}
      {generatedCount > 0 && (
        <div className="pb-28" data-testid="cross-pairs-section">
          <div className="flex items-center justify-between mb-2">
            <div className="font-black text-white">
              बनी हुई जोड़ियाँ <span className="text-cyan-300">({generatedCount})</span>
            </div>
            <button
              onClick={clearAll}
              data-testid="cross-clear-all"
              className="text-rose-400 text-sm font-bold active:scale-95"
            >
              Clear
            </button>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {activePairs.map(p => (
              <div
                key={p}
                className="relative rounded-xl border border-cyan-400/20 bg-black/40 px-2 py-1.5 flex items-center justify-between gap-1"
                data-testid={`cross-pair-${p}`}
              >
                <span className="font-black text-cyan-300 text-base tabular-nums">{p}</span>
                <span className="text-cyan-400 text-xs tabular-nums">₹{perAmt || 0}</span>
                <button
                  onClick={() => removePair(p)}
                  data-testid={`cross-pair-remove-${p}`}
                  className="w-5 h-5 rounded-md bg-rose-500/15 border border-rose-500/40 text-rose-400 flex items-center justify-center active:scale-90"
                  aria-label="Remove"
                >
                  <XIcon className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <BottomBar
        label="कुल पॉइंट्स"
        value={total}
        onProceed={openReview}
        canProceed={generatedCount > 0 && perAmt > 0 && market?.is_market_active}
      />

      <ConfirmBidsModal
        open={showReview}
        rows={rows}
        total={total}
        onClose={() => setShowReview(false)}
        onConfirm={confirmPlace}
        onDelete={removePair}
        submitting={submitting}
      />

      <BetWarningModal
        open={!!warn}
        type={warn?.type}
        title={warn?.title}
        message={warn?.message}
        hint={warn?.hint}
        onClose={() => setWarn(null)}
      />
    </Wrap>
  );
}

function Wrap({ embedded, children }) {
  if (embedded) return <>{children}</>;
  return <MobileLayout hideBottomNav hideHeader><div className="px-4 pt-4">{children}</div></MobileLayout>;
}
